// Historique de lecture, dans le localStorage. Une entrée par saison (par fiche
// chez voir-anime) : reprendre veut dire retrouver le dernier épisode vu, à la
// seconde près.

import type { Source } from "./constants.ts";
import type { Lang } from "./lang.ts";

const KEY = "aniweb-history";
const MAX = 30;

export interface Watched {
  key: string;
  source: Source;
  lang: Lang;
  animeName: string;
  animeUrl: string;
  /** anime-sama uniquement. */
  seasonName: string | null;
  seasonPath: string | null;
  /** Position dans la liste d'épisodes filtrée par langue. */
  episodePos: number;
  episodeLabel: string;
  position: number;
  duration: number;
  at: number;
}

export function entryKey(
  source: Source,
  animeUrl: string,
  seasonPath: string | null,
): string {
  return `${source}|${animeUrl}|${seasonPath ?? ""}`;
}

/** Une fiche voir-anime n'a pas de saison. Les entrées écrites avant la remise
 *  à zéro du chemin de saison en portaient tout de même un — celui d'une saison
 *  anime-sama ouverte auparavant — jusque dans leur clé. On les répare ici,
 *  sans quoi elles resteraient à côté de l'entrée correcte, en doublon figé.
 *  Quand la réparation rend deux entrées identiques, la plus récente gagne. */
function repair(entries: Watched[]): Watched[] {
  const kept = new Map<string, Watched>();

  for (const entry of entries) {
    const clean =
      entry.source === "voiranime" && entry.seasonPath !== null
        ? {
            ...entry,
            seasonName: null,
            seasonPath: null,
            key: entryKey(entry.source, entry.animeUrl, null),
          }
        : entry;

    const previous = kept.get(clean.key);
    if (!previous || previous.at < clean.at) kept.set(clean.key, clean);
  }

  return [...kept.values()];
}

function read(): Watched[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];

    const parsed: unknown = JSON.parse(raw);

    return Array.isArray(parsed) ? repair(parsed as Watched[]) : [];
  } catch {
    return [];
  }
}

function write(entries: Watched[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(entries.slice(0, MAX)));
  } catch {
    // quota plein ou stockage refusé : l'historique n'est pas essentiel
  }
}

/** Du plus récemment regardé au plus ancien. */
export function list(): Watched[] {
  return read().sort((a, b) => b.at - a.at);
}

export function find(key: string): Watched | null {
  return read().find((entry) => entry.key === key) ?? null;
}

export function save(entry: Watched): void {
  write([entry, ...read().filter((other) => other.key !== entry.key)]);
}

/** Met à jour la progression sans réécrire le reste de l'entrée. */
export function progress(key: string, position: number, duration: number): void {
  const entries = read();
  const entry = entries.find((other) => other.key === key);

  if (!entry) return;

  entry.position = position;
  entry.duration = duration;
  entry.at = Date.now();

  write(entries);
}

export function remove(key: string): void {
  write(read().filter((entry) => entry.key !== key));
}

/** Position de reprise, ou 0 : ni le tout début, ni un épisode déjà terminé. */
export function resumeAt(entry: Watched): number {
  if (entry.position < 15) return 0;
  if (entry.duration > 0 && entry.position > entry.duration - 30) return 0;

  return entry.position;
}

export function describe(entry: Watched): string {
  const parts = [entry.seasonName ?? entry.animeName, `Épisode ${entry.episodeLabel}`];

  if (entry.duration > 0) {
    parts.push(`${Math.round((entry.position / entry.duration) * 100)} %`);
  }

  return parts.join(" · ");
}
